import { Box, Button, Group, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { BookCopy, EllipsisVertical, TrashIcon, Undo } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';
import styles from '@/components/flow-drawer/components/flow-drawer-header-menu/flow-drawer-header-menu.module.css';
import useFlow from '@/hooks/use-flow.ts';
import useAppStore, { NotificationOperation } from '@/store/app.ts';
import useFlowStore from '@/store/flow.ts';
import type { TLibraryItemFrontend } from '@/types/common';

import Divider = Menu.Divider;

type FlowDrawerHeaderMenuProps = {
	isFromLibrary: boolean;
	originalCard?: TLibraryItemFrontend;
	handleRemove: () => void;
};
export const FlowDrawerHeaderMenu: FC<FlowDrawerHeaderMenuProps> = ({
	isFromLibrary,
	handleRemove,
	originalCard
}) => {
	const setNotification = useAppStore((state) => state.setNotification);
	const { flowItem, closeFlowDrawer } = useFlowStore(
		useShallow((state) => ({
			flowItem: state.flowDrawer.flowItem,
			closeFlowDrawer: state.closeFlowDrawer
		}))
	);
	const handleClose = useCallback(() => {
		modals.close('confirmation_modal');
	}, []);

	const { revertToOriginal, duplicateLibraryAsEditable } = useFlow();
	const handleConfirmRemove = () => {
		handleRemove();
		handleClose();
	};
	const handleClick = () => {
		if (isFromLibrary) {
			handleRemove();
			return;
		}

		modals.open({
			modalId: 'confirmation_modal',
			title: 'Confirm operation',
			children: (
				<Box className={styles.confirmModalWrapper}>
					<Text>
						Are you sure you want to remove this item? This action cannot be
						undone.
					</Text>

					<Group
						justify="flex-end"
						w="100%"
					>
						<Button onClick={handleClose}>Cancel</Button>

						<Button onClick={handleConfirmRemove}>Confirm</Button>
					</Group>
				</Box>
			),
			size: 'xl'
		});
	};

	const handleRevertCard = useCallback(() => {
		if (!flowItem?.source || !flowItem?.type || !originalCard) {
			return;
		}

		revertToOriginal(flowItem.source, flowItem?.type, originalCard);
		closeFlowDrawer();
		setNotification({
			data: flowItem.source,
			operation: NotificationOperation.REVERT_TO_ORIGINAL
		});
	}, [
		flowItem?.source,
		flowItem?.type,
		revertToOriginal,
		closeFlowDrawer,
		originalCard,
		setNotification
	]);

	const cloneLibraryItem = () => {
		if (!flowItem?.source) {
			return;
		}
		const newCanvasItem = {
			...flowItem.source,
			description: flowItem.source.description ?? '',
			idn: uuidv4(),
			is_from_library: false,
			origin_idn: flowItem.source.idn
		};
		duplicateLibraryAsEditable(newCanvasItem, flowItem.type);
		setNotification({
			data: flowItem.source,
			operation: NotificationOperation.CLONE
		});
		closeFlowDrawer();
	};
	return (
		<Menu withArrow>
			<Menu.Target>
				<Button
					opacity={0.5}
					p={0}
					variant="transparent"
				>
					<EllipsisVertical className={styles.icon} />
				</Button>
			</Menu.Target>

			<Menu.Dropdown>
				{originalCard ? (
					<>
						<Menu.Item
							leftSection={<Undo size={14} />}
							onClick={handleRevertCard}
						>
							Revert to the library item
						</Menu.Item>
						<Divider />
					</>
				) : undefined}

				{flowItem?.source.is_from_library ? (
					<>
						<Menu.Item
							leftSection={<BookCopy size={14} />}
							onClick={cloneLibraryItem}
						>
							Clone this library item
						</Menu.Item>
						<Divider />
					</>
				) : undefined}
				<Menu.Item
					color="red"
					leftSection={<TrashIcon size={14} />}
					onClick={handleClick}
					variant="danger"
				>
					{isFromLibrary ? 'Remove from canvas' : 'Remove'}
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
};
